/**
 * Lead schema — lead_capture_forms, leads tables, and related TS interfaces.
 *
 * Consumers:
 *  - apps/web/lib/drizzle/ — withSite()-scoped lead queries
 *  - apps/web/app/admin/leads/ — Server Actions validate form submissions
 *  - apps/web/app/api/leads/ — public submission endpoint reads FormFieldSpec
 *  - apps/web/app/admin/forms/ — routing config UI reads RoutingConfig shape
 *
 * FK cascade rules (intentional):
 *  - siteId → sites.id CASCADE: deleting a site removes all its leads + forms
 *  - formId → lead_capture_forms.id RESTRICT: can't delete a form that has leads
 *    (leads are audit-relevant historical records; archive the form instead)
 *  - vehicleId → vehicles.id SET NULL: deleting a vehicle preserves its leads;
 *    vehicleSnapshot captures vehicle context as it was at submission time
 *
 * Every row is site-scoped — all queries must pass through withSite().
 */

import { sql } from "drizzle-orm";
import {
  boolean,
  index,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { vehicles } from "./inventory";
import { sites } from "./tables";

// ----------------------------------------------------------------------------
// Enums
// ----------------------------------------------------------------------------

export const leadStatusEnum = pgEnum("lead_status", ["new", "read", "responded", "archived"]);

// ----------------------------------------------------------------------------
// TypeScript interfaces (stored as JSONB)
// ----------------------------------------------------------------------------

export interface FormFieldSpec {
  key: string;
  label: string;
  type: "text" | "email" | "tel" | "textarea" | "checkbox";
  required: boolean;
  maxLength?: number;
  placeholder?: string;
}

export interface RoutingConfig {
  db: { enabled: true }; // forward-compat sentinel — always true, never togglable
  plainEmail: { enabled: boolean; recipients: string[] };
  htmlEmail: { enabled: boolean; recipients: string[] };
  adfXmlEmail: { enabled: boolean; recipient: string | null };
  webhook: { enabled: boolean; url: string | null; secret: string | null };
}

export interface LeadSourceContext {
  referrer: string | null;
  utm: Record<string, string>;
  userAgent: string | null;
}

export interface VehicleSnapshot {
  vin: string;
  year: number;
  make: string;
  model: string;
  trim: string | null;
  priceCents: number | null;
  stockNumber: string | null;
}

// ----------------------------------------------------------------------------
// Tables
// ----------------------------------------------------------------------------

export const leadCaptureForms = pgTable(
  "lead_capture_forms",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    slug: text("slug").notNull(),
    displayName: text("display_name").notNull(),
    enabled: boolean("enabled").notNull().default(true),
    fields: jsonb("fields").$type<FormFieldSpec[]>().notNull(),
    routing: jsonb("routing").$type<RoutingConfig>().notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    siteSlugUnique: uniqueIndex("lead_capture_forms_site_slug_idx").on(t.siteId, t.slug),
  }),
);

export const leads = pgTable(
  "leads",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    formId: uuid("form_id")
      .notNull()
      .references(() => leadCaptureForms.id, { onDelete: "restrict" }),
    vehicleId: uuid("vehicle_id").references(() => vehicles.id, { onDelete: "set null" }),
    data: jsonb("data").$type<Record<string, unknown>>().notNull(),
    vehicleSnapshot: jsonb("vehicle_snapshot").$type<VehicleSnapshot | null>(),
    source: jsonb("source").$type<LeadSourceContext>().notNull(),
    status: leadStatusEnum("status").notNull().default("new"),
    idempotencyKey: text("idempotency_key"),
    submittedAt: timestamp("submitted_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    readAt: timestamp("read_at", { withTimezone: true }),
  },
  (t) => ({
    siteSubmittedIdx: index("leads_site_submitted_idx").on(t.siteId, t.submittedAt.desc()),
    siteVehicleIdx: index("leads_site_vehicle_idx").on(t.siteId, t.vehicleId),
    siteFormIdx: index("leads_site_form_idx").on(t.siteId, t.formId),
    siteStatusIdx: index("leads_site_status_idx").on(t.siteId, t.status),
    idempotencyUnique: uniqueIndex("leads_idempotency_idx")
      .on(t.siteId, t.formId, t.idempotencyKey)
      .where(sql`idempotency_key IS NOT NULL`),
  }),
);

// ----------------------------------------------------------------------------
// Inferred types
// ----------------------------------------------------------------------------

export type Lead = typeof leads.$inferSelect;
export type NewLead = typeof leads.$inferInsert;
export type LeadCaptureForm = typeof leadCaptureForms.$inferSelect;
export type NewLeadCaptureForm = typeof leadCaptureForms.$inferInsert;
